import { lazy, type ComponentType } from "react";

/**
 * A lazily-loaded route that survives a deploy landing under an open tab.
 *
 * THE FAILURE THIS EXISTS FOR, which code-splitting created on 2026-08-25.
 * `deploy-web.sh` syncs `dist/assets/` with `--delete`, so shipping a build
 * REMOVES the previous build's chunks. Before the signed-in app was split there
 * was one eager bundle: an open tab kept running it and a deploy was invisible.
 * Now a tab that loaded `index.html` before the deploy is holding hash names
 * that no longer exist, and the next navigation into a split route fetches a
 * 404. React's `lazy` surfaces that as a rejected promise, `ErrorBoundary`
 * catches it, and someone who did nothing wrong is told "That's on us."
 *
 * A missing chunk is almost never a broken app — it is a stale document. So
 * reload once: the fresh `index.html` names chunks that exist, and the
 * navigation completes. The person sees a flicker instead of an error page.
 *
 * ONE reload, guarded. Reload-on-failure without a guard is a reload LOOP —
 * every fresh document retries the same missing file and reloads again, pinning
 * the tab and hammering the origin, and the user cannot escape without closing
 * it. If the chunk is still missing after a full document load, something is
 * genuinely wrong and the error boundary is the honest answer.
 *
 * NOT COVERED BY THE E2E SUITE, deliberately, and worth saying why. The dev
 * server serves modules by source path rather than as hashed asset files, so it
 * cannot reproduce the condition; and in a production build every split route
 * sits behind auth, which needs a backend the smoke harness does not have. The
 * logic is unit-tested here instead. An e2e that "passes" without ever
 * intercepting the request would be worse than no test — the first attempt at
 * one did exactly that, reporting success while its route matcher never fired.
 *
 * Lives in its own module rather than in App.tsx so it can be tested without
 * importing the whole route tree — and because a module that exports both a
 * component and a helper is what dragged Account.tsx into the eager bundle.
 */

/** sessionStorage, not localStorage: the guard must not outlive the tab. */
export const CHUNK_RELOAD_KEY = "kev.chunk-reload";

/*
 * Every access is wrapped. Safari's private mode throws on sessionStorage, and a
 * storage exception must never be the thing that stops a route from loading —
 * failing open here costs at most one extra reload, which is strictly better
 * than never recovering.
 */
function alreadyReloaded(): boolean {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_KEY) !== null;
  } catch {
    return false;
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
  } catch {
    /* See above. */
  }
}

function clearReloaded(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    /* See above. */
  }
}

/**
 * The loader half, separated from `lazy()` so it can be tested directly.
 *
 * Returns the module, or — when a load fails for the first time — a promise that
 * NEVER SETTLES, because the document is already being replaced. Resolving with
 * anything would render a route for a fraction of a second on a page that is
 * unloading; Suspense simply keeps showing its fallback until the reload lands.
 */
export async function loadOrReload<T>(load: () => Promise<T>): Promise<T> {
  try {
    const mod = await load();
    /* Cleared on success, so a LATER stale chunk still gets its own one reload
       rather than inheriting a flag set hours ago. */
    clearReloaded();
    return mod;
  } catch (error) {
    if (alreadyReloaded()) throw error;
    markReloaded();
    window.location.reload();
    return new Promise<never>(() => {});
  }
}

export function lazyRoute(load: () => Promise<{ default: ComponentType }>) {
  return lazy(() => loadOrReload(load));
}

/**
 * The same thing for a route that TAKES a prop — added 2026-09-08 for the
 * fixture page, which is now rendered once per sport off
 * `config/fixturePages.ts` and is handed its sport by the row that produced
 * its path rather than reading it back out of the URL.
 *
 * ⚠️ A SECOND FUNCTION RATHER THAN A TYPE PARAMETER ON THE ONE ABOVE. Making
 * `lazyRoute` generic broke every one of its two dozen existing call sites:
 * the `.then((m) => ({ default: m.X }))` callback is contextually typed, so
 * TypeScript infers the parameter as `never` for a prop-less component and then
 * refuses to assign it to the default. The type argument here is always given
 * explicitly, which is what avoids the inference entirely — and prop-less
 * routes keep the signature that already says what they are.
 */
export function lazyRouteWithProps<P extends object>(
  load: () => Promise<{ default: ComponentType<P> }>,
) {
  return lazy(() => loadOrReload(load));
}
